// The voice page (pages/[model].js) and the sections only it renders
// (TryVoiceSection, LibraryDemosSection, ModelCommentSection/Item,
// ModelCollectionsWithVoice, AddToCollectionModal). One template, ~90,000
// pages, so the SEO strings matter: they follow what people search ("voz de
// goku ia", "voz ia de ...", BR "voz do ... ia"). Portuguese avoids "do/da"
// because the voice's gender is unknown. See messages/README.md.
export const voice = {
  en: {
    // <head> and JSON-LD
    "voice.seo.title": "{name} AI Voice: Hear It & Make AI Covers | Jammable",
    "voice.seo.description":
      "Listen to {name} AI covers and make your own in about a minute. Use the community-made {name} voice for songs, text-to-speech and duets on Jammable.",
    "voice.seo.appName": "{name} AI Voice Generator",
    "voice.seo.private": "Private voice | Jammable",
    "voice.name": "{name} AI Voice",

    // Removed / private
    "voice.removed": "This voice has been removed.",
    "voice.private": "This voice is private.",
    "voice.browseOther": "Browse other voices",

    "voice.error": "Something went wrong. Please try again",
    "voice.copied": "Copied to clipboard!",

    // Hero
    "voice.newer.body":
      "{username} has updated this voice. Try the newer version",
    "voice.newer.cta": "Newer version",
    "voice.communityFavourite": "Community favourite",
    // {name} is replaced by the uploader's name, styled separately.
    "voice.uploadedBy": "Uploaded By {name}",
    "voice.voices.one": "{n} voice",
    "voice.voices.other": "{n} voices",
    "voice.createCover": "Create a cover · {uses} uses",
    "voice.like": "Like voice",
    "voice.unlike": "Unlike voice",
    "voice.comments": "Comments",
    "voice.saveToCollection": "Save to collection",
    "voice.share": "Share Voice",
    "voice.edit": "Edit Voice",
    "voice.report": "Report Voice",
    "voice.delete": "Delete Voice",
    "voice.more": "More",
    "voice.description": "Description",

    "voice.discord.label": "Talk {name} with the community",
    "voice.discord.labelNoName": "Talk AI voices with the community",
    "voice.discord.body":
      "Share your covers, request new voices and get tips on Discord",

    // Related voices. {badge} is the voice's category as stored (English).
    "voice.similar.title": "Similar {name} AI Voices",
    "voice.similar.sub": "Users commonly used these voices with {name}.",
    "voice.similar.moreHub": "More {badge} voices",
    "voice.popularHub.title": "Popular {badge} AI voices",
    "voice.seeAll": "See all",
    "voice.collections.title": "Collections with {name}",
    "voice.collections.titleNoName": "Collections With This Voice",
    "voice.pairWell.title": "Voices that pair well with {name}",

    // About
    "voice.about.title": "About the {name} AI Voice",
    "voice.about.use":
      "Use the {name} AI voice to create AI covers, as a voice changer, or to generate text-to-speech.",
    "voice.about.categorised": "It's categorised as {list}.",
    "voice.about.uses": "This voice has been used {n} times on Jammable.",
    "voice.howTo.title": "How to make a {name} AI cover",

    // FAQ
    "voice.faq.title": "{name} AI Voice FAQ",
    "voice.faq.sub": "Everything about making covers with the {name} voice.",
    "voice.link.aiCover": "AI Cover Generator",
    "voice.link.voiceChanger": "AI Voice Changer",
    "voice.link.browseAll": "Browse all AI voices",
    "voice.link.weights": "Weights.gg alternative",
    "voice.faq.how.q": "How do I make {name} AI covers?",
    // {dmca} becomes the DMCA Policy link.
    "voice.faq.how.a":
      "In order to make {name} AI covers, you need to add your own audio in the studio (please read our {dmca}). Once you've done so, press ready to convert! Your {name} AI cover will be ready in around 30-60 seconds.",
    "voice.faq.dmca": "DMCA Policy",
    "voice.faq.input.q": "What input can I use for my {name} AI covers?",
    "voice.faq.input.a":
      "You can only upload your own original audio. You should not input any sound recordings that contain third party copyright material without the owner's permission.",
    "voice.faq.time.q": "How long will my {name} AI cover take?",
    "voice.faq.time.a":
      "Your {name} AI cover will be ready in around 30-60 seconds!",
    "voice.faq.credits.q": "How many credits does each {name} AI cover use?",
    "voice.faq.credits.a": "Each {name} AI cover will use just 1 credit!",
    "voice.faq.acapella.q": "Do I need to upload an acapella?",
    "voice.faq.acapella.a":
      "No, you do not need to upload an acapella as our system will extract the acapella regardless for your {name} AI cover!",
    "voice.faq.changer.q": "Can I use the {name} AI voice as a voice changer?",
    "voice.faq.changer.a":
      "Yes. You can use the {name} AI voice as a voice changer or for text-to-speech — upload or record your audio and convert it into the {name} voice in seconds.",
    "voice.faq.free.q": "Is the {name} AI voice free?",
    "voice.faq.free.a":
      "You can listen to the {name} AI voice demos and set up a cover for free. Making covers needs a Jammable plan, which gives you unlimited {name} AI covers, downloads and more.",

    // TryVoiceSection
    "voice.demos.title": "Hear it first",
    "voice.demos.note":
      "Demos are auto-generated to show the quality of voices.",
    "voice.demos.maleSinging": "Male Singing {n}",
    "voice.demos.femaleSinging": "Female Singing {n}",
    "voice.demos.english": "English",
    "voice.play": "Play {name}",
    "voice.pause": "Pause {name}",

    // LibraryDemosSection
    "voice.library.title": "Songs with this voice",
    "voice.library.sub":
      "Listen to the AI {name} voice singing popular songs from our library.",
    "voice.library.unknownSong": "Unknown Song",
    "voice.library.unknownArtist": "Unknown Artist",

    // ModelCommentSection / ModelCommentItem
    "voice.comments.count.one": "{n} comment",
    "voice.comments.count.other": "{n} comments",
    "voice.comments.add": "Add a comment",
    "voice.comments.post": "Post",
    "voice.comments.top": "Top",
    "voice.comments.newest": "Newest",
    "voice.comments.empty": "No comments here...",
    "voice.comments.emptySub": "Start the conversation!",
    "voice.comments.more": "Show more comments",
    "voice.comments.posted": "Comment posted!",
    "voice.comments.avatarAlt": "User Avatar",
    "voice.comments.creator": "Creator",
    "voice.comments.pinned": "Pinned",
    "voice.comments.reply": "Reply",
    "voice.comments.replyLabel": "Comment Reply",
    "voice.comments.replyPlaceholder": "What do you think?!",
    "voice.comments.viewReply.one": "View {n} Reply",
    "voice.comments.viewReply.other": "View {n} Replies",
    "voice.comments.viewMore": "View {n} More",
    "voice.comments.justNow": "just now",
    "voice.comments.minutes": "{n}m",
    "voice.comments.hours": "{n}h",
    "voice.comments.days": "{n}d",
    "voice.comments.repliesFailed": "Failed to load replies",
    "voice.comments.pinnedToast": "Comment pinned!",
    "voice.comments.unpinnedToast": "Comment unpinned!",
    "voice.comments.pinFailed": "Failed to pin comment",
    "voice.comments.pin": "Pin Comment",
    "voice.comments.unpin": "Unpin Comment",
    "voice.comments.delete": "Delete Comment",
    "voice.comments.report": "Report Comment",

    // AddToCollectionModal
    "voice.addTo.title": "Add to Collection",
    "voice.addTo.error": "Error loading collections: {message}",
    "voice.addTo.empty": "No collections found...",
    "voice.addTo.emptySub": "Create your first collection!",
    "voice.addTo.new": "New Collection",
  },
  es: {
    "voice.seo.title":
      "Voz de {name} con IA: escúchala y haz covers | Jammable",
    "voice.seo.description":
      "Escucha covers con la voz de {name} y haz los tuyos en un minuto. Usa la voz IA de {name} creada por la comunidad para canciones, texto a voz y duetos en Jammable.",
    "voice.seo.appName": "Generador de voz IA de {name}",
    "voice.seo.private": "Voz privada | Jammable",
    "voice.name": "Voz de {name} con IA",

    "voice.removed": "Esta voz fue eliminada.",
    "voice.private": "Esta voz es privada.",
    "voice.browseOther": "Explorar otras voces",

    "voice.error": "Algo salió mal. Inténtalo de nuevo",
    "voice.copied": "¡Copiado al portapapeles!",

    "voice.newer.body":
      "{username} actualizó esta voz. Prueba la versión más nueva",
    "voice.newer.cta": "Versión más nueva",
    "voice.communityFavourite": "Favorita de la comunidad",
    "voice.uploadedBy": "Subida por {name}",
    "voice.voices.one": "{n} voz",
    "voice.voices.other": "{n} voces",
    "voice.createCover": "Crear un cover · {uses} usos",
    "voice.like": "Me gusta",
    "voice.unlike": "Quitar me gusta",
    "voice.comments": "Comentarios",
    "voice.saveToCollection": "Guardar en colección",
    "voice.share": "Compartir voz",
    "voice.edit": "Editar voz",
    "voice.report": "Reportar voz",
    "voice.delete": "Eliminar voz",
    "voice.more": "Más",
    "voice.description": "Descripción",

    "voice.discord.label": "Habla de {name} con la comunidad",
    "voice.discord.labelNoName": "Habla de voces IA con la comunidad",
    "voice.discord.body":
      "Comparte tus covers, pide voces nuevas y recibe consejos en Discord",

    "voice.similar.title": "Voces IA similares a {name}",
    "voice.similar.sub": "Los usuarios suelen usar estas voces con {name}.",
    "voice.similar.moreHub": "Más voces de {badge}",
    "voice.popularHub.title": "Voces IA populares de {badge}",
    "voice.seeAll": "Ver todas",
    "voice.collections.title": "Colecciones con {name}",
    "voice.collections.titleNoName": "Colecciones con esta voz",
    "voice.pairWell.title": "Voces que combinan bien con {name}",

    "voice.about.title": "Sobre la voz de {name} con IA",
    "voice.about.use":
      "Usa la voz IA de {name} para hacer covers con IA, como cambiador de voz o para generar texto a voz.",
    "voice.about.categorised": "Está en las categorías {list}.",
    "voice.about.uses": "Esta voz se ha usado {n} veces en Jammable.",
    "voice.howTo.title": "Cómo hacer un cover con la voz IA de {name}",

    "voice.faq.title": "Preguntas frecuentes sobre la voz IA de {name}",
    "voice.faq.sub": "Todo sobre cómo hacer covers con la voz de {name}.",
    "voice.link.aiCover": "Generador de covers con IA",
    "voice.link.voiceChanger": "Cambiador de voz con IA",
    "voice.link.browseAll": "Ver todas las voces IA",
    "voice.link.weights": "Alternativa a Weights.gg",
    "voice.faq.how.q": "¿Cómo hago covers con la voz IA de {name}?",
    "voice.faq.how.a":
      "Para hacer covers con la voz IA de {name}, agrega tu propio audio en el estudio (lee nuestra {dmca}). Cuando lo hayas hecho, ¡dale a convertir! Tu cover con la voz IA de {name} estará listo en unos 30-60 segundos.",
    "voice.faq.dmca": "Política DMCA",
    "voice.faq.input.q":
      "¿Qué audio puedo usar para mis covers con la voz IA de {name}?",
    "voice.faq.input.a":
      "Solo puedes subir tu propio audio original. No debes subir grabaciones que contengan material con derechos de autor de terceros sin el permiso del titular.",
    "voice.faq.time.q": "¿Cuánto tarda mi cover con la voz IA de {name}?",
    "voice.faq.time.a":
      "¡Tu cover con la voz IA de {name} estará listo en unos 30-60 segundos!",
    "voice.faq.credits.q":
      "¿Cuántos créditos usa cada cover con la voz IA de {name}?",
    "voice.faq.credits.a":
      "¡Cada cover con la voz IA de {name} usa solo 1 crédito!",
    "voice.faq.acapella.q": "¿Necesito subir un acapella?",
    "voice.faq.acapella.a":
      "No, no necesitas subir un acapella: nuestro sistema extrae la voz de todas formas para tu cover con la voz IA de {name}.",
    "voice.faq.changer.q":
      "¿Puedo usar la voz IA de {name} como cambiador de voz?",
    "voice.faq.changer.a":
      "Sí. Puedes usar la voz IA de {name} como cambiador de voz o para texto a voz: sube o graba tu audio y conviértelo a la voz de {name} en segundos.",
    "voice.faq.free.q": "¿La voz IA de {name} es gratis?",
    "voice.faq.free.a":
      "Puedes escuchar los demos de la voz IA de {name} y preparar un cover gratis. Para hacer covers necesitas un plan de Jammable, que te da covers ilimitados con la voz IA de {name}, descargas y más.",

    "voice.demos.title": "Escúchala primero",
    "voice.demos.note":
      "Los demos se generan automáticamente para mostrar la calidad de las voces.",
    "voice.demos.maleSinging": "Canto masculino {n}",
    "voice.demos.femaleSinging": "Canto femenino {n}",
    "voice.demos.english": "Inglés",
    "voice.play": "Reproducir {name}",
    "voice.pause": "Pausar {name}",

    "voice.library.title": "Canciones con esta voz",
    "voice.library.sub":
      "Escucha la voz IA de {name} cantando canciones populares de nuestra biblioteca.",
    "voice.library.unknownSong": "Canción desconocida",
    "voice.library.unknownArtist": "Artista desconocido",

    "voice.comments.count.one": "{n} comentario",
    "voice.comments.count.other": "{n} comentarios",
    "voice.comments.add": "Agrega un comentario",
    "voice.comments.post": "Publicar",
    "voice.comments.top": "Destacados",
    "voice.comments.newest": "Más recientes",
    "voice.comments.empty": "Aún no hay comentarios...",
    "voice.comments.emptySub": "¡Empieza la conversación!",
    "voice.comments.more": "Ver más comentarios",
    "voice.comments.posted": "¡Comentario publicado!",
    "voice.comments.avatarAlt": "Foto de perfil",
    "voice.comments.creator": "Creador",
    "voice.comments.pinned": "Fijado",
    "voice.comments.reply": "Responder",
    "voice.comments.replyLabel": "Respuesta al comentario",
    "voice.comments.replyPlaceholder": "¿Qué opinas?",
    "voice.comments.viewReply.one": "Ver {n} respuesta",
    "voice.comments.viewReply.other": "Ver {n} respuestas",
    "voice.comments.viewMore": "Ver {n} más",
    "voice.comments.justNow": "ahora",
    "voice.comments.minutes": "{n} min",
    "voice.comments.hours": "{n} h",
    "voice.comments.days": "{n} d",
    "voice.comments.repliesFailed": "No se pudieron cargar las respuestas",
    "voice.comments.pinnedToast": "¡Comentario fijado!",
    "voice.comments.unpinnedToast": "¡Comentario desfijado!",
    "voice.comments.pinFailed": "No se pudo fijar el comentario",
    "voice.comments.pin": "Fijar comentario",
    "voice.comments.unpin": "Desfijar comentario",
    "voice.comments.delete": "Eliminar comentario",
    "voice.comments.report": "Reportar comentario",

    "voice.addTo.title": "Agregar a una colección",
    "voice.addTo.error": "Error al cargar las colecciones: {message}",
    "voice.addTo.empty": "No se encontraron colecciones...",
    "voice.addTo.emptySub": "¡Crea tu primera colección!",
    "voice.addTo.new": "Nueva colección",
  },
  pt: {
    "voice.seo.title": "Voz de {name} com IA: ouça e faça covers | Jammable",
    "voice.seo.description":
      "Ouça covers com a voz de {name} e faça os seus em cerca de um minuto. Use a voz de IA de {name} criada pela comunidade para músicas, texto para fala e duetos no Jammable.",
    "voice.seo.appName": "Gerador de voz de IA de {name}",
    "voice.seo.private": "Voz privada | Jammable",
    "voice.name": "Voz de {name} com IA",

    "voice.removed": "Esta voz foi removida.",
    "voice.private": "Esta voz é privada.",
    "voice.browseOther": "Ver outras vozes",

    "voice.error": "Algo deu errado. Tente novamente",
    "voice.copied": "Copiado para a área de transferência!",

    "voice.newer.body":
      "{username} atualizou esta voz. Experimente a versão mais nova",
    "voice.newer.cta": "Versão mais nova",
    "voice.communityFavourite": "Favorita da comunidade",
    "voice.uploadedBy": "Enviada por {name}",
    "voice.voices.one": "{n} voz",
    "voice.voices.other": "{n} vozes",
    "voice.createCover": "Criar um cover · {uses} usos",
    "voice.like": "Curtir voz",
    "voice.unlike": "Descurtir voz",
    "voice.comments": "Comentários",
    "voice.saveToCollection": "Salvar na coleção",
    "voice.share": "Compartilhar voz",
    "voice.edit": "Editar voz",
    "voice.report": "Denunciar voz",
    "voice.delete": "Excluir voz",
    "voice.more": "Mais",
    "voice.description": "Descrição",

    "voice.discord.label": "Converse sobre {name} com a comunidade",
    "voice.discord.labelNoName": "Converse sobre vozes de IA com a comunidade",
    "voice.discord.body":
      "Compartilhe seus covers, peça vozes novas e receba dicas no Discord",

    "voice.similar.title": "Vozes de IA parecidas com {name}",
    "voice.similar.sub": "Os usuários costumam usar estas vozes com {name}.",
    "voice.similar.moreHub": "Mais vozes de {badge}",
    "voice.popularHub.title": "Vozes de IA populares de {badge}",
    "voice.seeAll": "Ver todas",
    "voice.collections.title": "Coleções com {name}",
    "voice.collections.titleNoName": "Coleções com esta voz",
    "voice.pairWell.title": "Vozes que combinam com {name}",

    "voice.about.title": "Sobre a voz de {name} com IA",
    "voice.about.use":
      "Use a voz de IA de {name} para fazer covers com IA, como modificador de voz ou para gerar texto para fala.",
    "voice.about.categorised": "Está nas categorias {list}.",
    "voice.about.uses": "Esta voz já foi usada {n} vezes no Jammable.",
    "voice.howTo.title": "Como fazer um cover com a voz de IA de {name}",

    "voice.faq.title": "Perguntas frequentes sobre a voz de IA de {name}",
    "voice.faq.sub": "Tudo sobre como fazer covers com a voz de {name}.",
    "voice.link.aiCover": "Gerador de covers com IA",
    "voice.link.voiceChanger": "Modificador de voz com IA",
    "voice.link.browseAll": "Ver todas as vozes de IA",
    "voice.link.weights": "Alternativa ao Weights.gg",
    "voice.faq.how.q": "Como faço covers com a voz de IA de {name}?",
    "voice.faq.how.a":
      "Para fazer covers com a voz de IA de {name}, adicione seu próprio áudio no estúdio (leia nossa {dmca}). Depois é só clicar para converter! Seu cover com a voz de IA de {name} fica pronto em cerca de 30 a 60 segundos.",
    "voice.faq.dmca": "Política DMCA",
    "voice.faq.input.q":
      "Que áudio posso usar nos meus covers com a voz de IA de {name}?",
    "voice.faq.input.a":
      "Você só pode enviar seu próprio áudio original. Não envie gravações que contenham material protegido por direitos autorais de terceiros sem a permissão do titular.",
    "voice.faq.time.q":
      "Quanto tempo leva meu cover com a voz de IA de {name}?",
    "voice.faq.time.a":
      "Seu cover com a voz de IA de {name} fica pronto em cerca de 30 a 60 segundos!",
    "voice.faq.credits.q":
      "Quantos créditos cada cover com a voz de IA de {name} usa?",
    "voice.faq.credits.a":
      "Cada cover com a voz de IA de {name} usa só 1 crédito!",
    "voice.faq.acapella.q": "Preciso enviar um acapella?",
    "voice.faq.acapella.a":
      "Não, você não precisa enviar um acapella: nosso sistema extrai a voz de qualquer jeito para o seu cover com a voz de IA de {name}!",
    "voice.faq.changer.q":
      "Posso usar a voz de IA de {name} como modificador de voz?",
    "voice.faq.changer.a":
      "Sim. Você pode usar a voz de IA de {name} como modificador de voz ou para texto para fala: envie ou grave seu áudio e converta para a voz de {name} em segundos.",
    "voice.faq.free.q": "A voz de IA de {name} é grátis?",
    "voice.faq.free.a":
      "Você pode ouvir as demos da voz de IA de {name} e preparar um cover de graça. Para fazer covers, você precisa de um plano do Jammable, que dá covers ilimitados com a voz de IA de {name}, downloads e mais.",

    "voice.demos.title": "Ouça primeiro",
    "voice.demos.note":
      "As demos são geradas automaticamente para mostrar a qualidade das vozes.",
    "voice.demos.maleSinging": "Canto masculino {n}",
    "voice.demos.femaleSinging": "Canto feminino {n}",
    "voice.demos.english": "Inglês",
    "voice.play": "Tocar {name}",
    "voice.pause": "Pausar {name}",

    "voice.library.title": "Músicas com esta voz",
    "voice.library.sub":
      "Ouça a voz de IA de {name} cantando músicas populares da nossa biblioteca.",
    "voice.library.unknownSong": "Música desconhecida",
    "voice.library.unknownArtist": "Artista desconhecido",

    "voice.comments.count.one": "{n} comentário",
    "voice.comments.count.other": "{n} comentários",
    "voice.comments.add": "Adicione um comentário",
    "voice.comments.post": "Publicar",
    "voice.comments.top": "Em destaque",
    "voice.comments.newest": "Mais recentes",
    "voice.comments.empty": "Nenhum comentário ainda...",
    "voice.comments.emptySub": "Comece a conversa!",
    "voice.comments.more": "Ver mais comentários",
    "voice.comments.posted": "Comentário publicado!",
    "voice.comments.avatarAlt": "Foto de perfil",
    "voice.comments.creator": "Criador",
    "voice.comments.pinned": "Fixado",
    "voice.comments.reply": "Responder",
    "voice.comments.replyLabel": "Resposta ao comentário",
    "voice.comments.replyPlaceholder": "O que você achou?",
    "voice.comments.viewReply.one": "Ver {n} resposta",
    "voice.comments.viewReply.other": "Ver {n} respostas",
    "voice.comments.viewMore": "Ver mais {n}",
    "voice.comments.justNow": "agora",
    "voice.comments.minutes": "{n} min",
    "voice.comments.hours": "{n} h",
    "voice.comments.days": "{n} d",
    "voice.comments.repliesFailed": "Não foi possível carregar as respostas",
    "voice.comments.pinnedToast": "Comentário fixado!",
    "voice.comments.unpinnedToast": "Comentário desafixado!",
    "voice.comments.pinFailed": "Não foi possível fixar o comentário",
    "voice.comments.pin": "Fixar comentário",
    "voice.comments.unpin": "Desafixar comentário",
    "voice.comments.delete": "Excluir comentário",
    "voice.comments.report": "Denunciar comentário",

    "voice.addTo.title": "Adicionar à coleção",
    "voice.addTo.error": "Erro ao carregar as coleções: {message}",
    "voice.addTo.empty": "Nenhuma coleção encontrada...",
    "voice.addTo.emptySub": "Crie sua primeira coleção!",
    "voice.addTo.new": "Nova coleção",
  },
};
